import { compose, withHooks, withTranslation } from "enhancers";
import {
  Box,
  BrowseButton,
  Button,
  Field,
  IconButton,
  ImageLightbox,
  MaterialIcon,
  Modal,
  Typography,
} from "components";
import ImageUpload from "./ImageUpload";
import { get, range, map } from "lodash";
import Authorize from "components/Authorize";

import { ReactComponent as AddImageIcon } from "assets/icon/add-icon.svg";
import { AppColor } from "theme/app-color";
import { PERMISSIONS } from "constants/enums/permissions";
import { BoxProps } from "@material-ui/core";
import { t, TFunction } from "i18next";
import { useMemo } from "react";

const FileNameComponent = ({ fileName, handleRemoveImage }: any) => {
  return (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="flex-end"
      alignItems="center"
      mb={4}
    >
      <Box width={150}>
        <Typography
          title={fileName}
          color={AppColor["Other/Info"]}
          // variant="Body/16"
          style={{
            textDecoration: "underline",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontSize: "14px",
          }}
        >
          {fileName}
        </Typography>
      </Box>

      <IconButton onClick={handleRemoveImage} style={{ padding: 2 }}>
        <MaterialIcon
          name="DeleteOutline"
          color="error"
          style={{ fontSize: "16px" }}
        />
      </IconButton>
    </Box>
  );
};

interface LogoUploadProps extends BoxProps {
  t: TFunction;
  noStartIcon: any;
  amount: number;
  name: string;
  hasEditPermission: string;
  canAddMore: boolean;
  addImage: any;
  addButtonLabel: string;
  hasEditButton: boolean;
  fileName: string;
  showRemoveButton: boolean;
  handleRemoveImage: () => void;
  label: string;
  required: boolean;
}

const LogoUpload = (props: LogoUploadProps) => (
  <Box
    width="100%"
    display={props.canAddMore ? "flex" : "block"}
    justifyContent="space-between"
  >
    <Box display="flex" flexDirection="row" justifyContent="space-between">
      <Box>
        {props.label && (
          <Typography variant="Body/16" color={AppColor["Text/Primary"]}>
            {props.label}
            {props.required && <span style={{ color: "red" }}>*</span>}
          </Typography>
        )}
      </Box>

      {props.fileName && props.showRemoveButton && (
        <FileNameComponent
          fileName={props.fileName}
          handleRemoveImage={props.handleRemoveImage}
        />
      )}
    </Box>

    <Box>
      {range(props.amount).map((index) => {
        return (
          <Box
            key={index}
            mb={props.amount > 1 ? 2 : 0}
            mr={props.amount > 1 ? 6 : 0}
          >
            {/* <Box onClick={() => props.openImageLightbox(index)}> */}
            <Field
              component={ImageUpload}
              {...props}
              name={`${props.name}[${index}]`}
            />
            {/* </Box> */}
          </Box>
        );
      })}
    </Box>

    {props.hasEditButton && (
      <Box display="flex">
        <Box display="flex" flexDirection="column">
          {/* <Authorize permissions={[PERMISSIONS.SETTING_MANAGEMENT_EDIT]}> */}
          <BrowseButton
            variant="outlined"
            startIcon={props.noStartIcon ? "" : <AddImageIcon />}
            onBrowse={props.addImage}
            accept="image/*"
            disabled={!props.hasEditPermission}
          >
            {props.canAddMore ? props.addButtonLabel : props.t(".editImage")}
          </BrowseButton>
          {/* </Authorize> */}
        </Box>
      </Box>
    )}
  </Box>
);

const enhancer = compose(
  withTranslation({ prefix: "pages.main.settings.index" }),
  withHooks((props: any, hooks: any) => {
    const {
      form,
      name,
      insert,
      remove,
      title,
      addButtonLabel,
      mt,
      label,
      onRemoveImage,
      hasEditPermission,
      noStartIcon,
      showEditButton = true,
      removeSingleImage = false,
      ...rest
    } = props;
    const { useCallback } = hooks;
    const values = get(form.values, name);
    const amount = values?.length ?? 0;

    const canAddMore = amount < 1;

    const confirmRemoveImage = useCallback(
      (index: number) => {
        remove(index);
        if (onRemoveImage) {
          onRemoveImage(index, props);
        }
      },
      [onRemoveImage, props, remove]
    );

    const removeImage = useCallback(
      (index: number) => {
        Modal.confirm({
          title: "ลบข้อมูล",
          children: "ต้องการลบรูปภาพหรือไม่?",
          onOk: ({ close }: any) => {
            confirmRemoveImage(index);
            close();
          },
          okButtonVariant: "contained",
          cancelButtonVariant: "outlined",
          cancelButtonLabel: "ยกเลิก",
          okButtonLabel: "ลบ",
        });
      },
      [confirmRemoveImage]
    );

    const addImage = useCallback(
      (file: any) => {
        if (canAddMore) {
          insert(amount, file);
        } else {
          removeImage();
          insert(amount, file);
        }
      },
      [insert, amount, canAddMore, removeImage]
    );

    const openImageLightbox = useCallback(
      (index: number) => {
        const images = map(values, (file, index) => {
          return {
            url: file instanceof File ? URL.createObjectURL(file) : null,
            title: `รูปที่ ${index + 1}`,
          };
        });
        ImageLightbox.open({
          images,
          startIndex: index,
        });
      },
      [values]
    );

    const hasEditButton = useMemo(() => {
      if (canAddMore) return true;
      if (removeSingleImage) return showEditButton;
      return true;
    }, [canAddMore, removeSingleImage, showEditButton]);

    const showRemoveButton = useMemo(() => {
      if (canAddMore) return false;
      if (removeSingleImage) return values?.length > 0;
      return false;
    }, [canAddMore, removeSingleImage, values?.length]);

    const handleRemoveImage = useCallback(() => {
      removeImage();
    }, [removeImage]);

    const fileName = useMemo(() => {
      if (canAddMore) return "";
      return values[0]?.name;
    }, [canAddMore, values]);

    return {
      name,
      amount,
      addImage,
      removeImage,
      title,
      addButtonLabel,
      mt,
      label,
      canAddMore,
      openImageLightbox,
      hasEditPermission,
      noStartIcon,
      hasEditButton,

      fileName,
      showRemoveButton,
      handleRemoveImage,
      ...rest,
    };
  })
);

export default enhancer(LogoUpload);
